import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

const SIZE_DEBUG = true;
let sizeDebugSeq = 0;

function sizeDebug(label, data) {
  if (!SIZE_DEBUG) return;
  const payload = data === undefined ? '' : data;
}

function snapshotSizeState(root = document) {
  const pickers =
    root instanceof Element && root.matches?.('variant-picker')
      ? [root]
      : Array.from(root.querySelectorAll('variant-picker'));
  return pickers.map((picker, index) => {
    const sizeFieldset = Array.from(picker.querySelectorAll('fieldset.variant-option')).find((fieldset) => {
      const legend = fieldset.querySelector('legend');
      return legend?.textContent?.toLowerCase().includes('size');
    });
    const radios = Array.from((sizeFieldset || picker).querySelectorAll('input[type="radio"]'));
    const checked = radios.filter((radio) => radio.checked);
    const formVariant = picker.closest('.shopify-section')?.querySelector('input[name="id"], [ref="variantId"]');
    const jsonScript = picker.querySelector('script[type="application/json"]');
    let jsonVariant = null;
    try {
      jsonVariant = jsonScript?.textContent ? JSON.parse(jsonScript.textContent) : null;
    } catch {
      jsonVariant = { parseError: true };
    }

    return {
      pickerIndex: index,
      pickerProductUrl: picker.dataset.productUrl,
      pickerProductId: picker.dataset.productId,
      sizeRadios: radios.map((radio) => ({
        value: radio.value,
        checked: radio.checked,
        currentChecked: radio.dataset.currentChecked,
        variantId: radio.dataset.variantId,
        optionValueId: radio.dataset.optionValueId,
        connectedProductUrl: radio.dataset.connectedProductUrl || null,
        fieldsetIndex: radio.dataset.fieldsetIndex,
        inputIndex: radio.dataset.inputIndex,
        name: radio.name,
        isConnected: radio.isConnected,
      })),
      checkedValues: checked.map((radio) => radio.value),
      checkedVariantIds: checked.map((radio) => radio.dataset.variantId),
      formVariantId: formVariant?.value || formVariant?.dataset?.variantId || null,
      jsonVariant: jsonVariant
        ? { id: jsonVariant.id, title: jsonVariant.title, option1: jsonVariant.option1, option2: jsonVariant.option2, option3: jsonVariant.option3 }
        : null,
      location: window.location.pathname + window.location.search,
    };
  });
}

const productJsonCache = new Map();

function getProductOptionNames(productData) {
  return (productData.options || []).map((option) => {
    if (typeof option === 'string') return option;
    return option?.name || '';
  });
}

function normalizeProductUrl(productUrl) {
  if (!productUrl) return '';
  return productUrl.split('?')[0].replace(/\/$/, '');
}

async function fetchProductData(productUrl) {
  const normalizedProductUrl = normalizeProductUrl(productUrl);

  if (!normalizedProductUrl) {
    throw new Error('Missing product URL');
  }

  if (!productJsonCache.has(normalizedProductUrl)) {
    const productJsonUrl = `${normalizedProductUrl}.js`;
    const request = fetch(productJsonUrl).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch product JSON: ${response.status}`);
      }

      return response.json();
    });

    productJsonCache.set(normalizedProductUrl, request);
  }

  return productJsonCache.get(normalizedProductUrl);
}

function findMatchingVariant(productData, selectedOptions) {
  const optionNames = getProductOptionNames(productData);

  return productData.variants?.find((variant) =>
    optionNames.every((optionName, index) => {
      const selectedValue = selectedOptions.get(optionName);

      if (!selectedValue) return true;

      return variant.options?.[index] === selectedValue;
    })
  );
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {number} */
  #availabilityRequestId = 0;

  /** @type {{ optionValueIds: string[]; variantId: string | undefined } | undefined} */
  #requestedSelection;

  /** @type {Map<string, Array<{ options: string[]; available: boolean }>>} */
  #colorVariantCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    sizeDebug('connectedCallback', {
      alreadyHadRadios: this.#radios.length,
      fieldsetCount: fieldsets.length,
      stack: new Error().stack,
    });

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    queueMicrotask(() => {
      this.updateCombinedListingSwatchAvailability();
    });

    sizeDebug('connectedCallback snapshot', snapshotSizeState(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    sizeDebugSeq += 1;
    const seq = sizeDebugSeq;
    sizeDebug('variantChanged START', {
      seq,
      isTrusted: event.isTrusted,
      targetValue: 'value' in event.target ? event.target.value : null,
      targetName: event.target.getAttribute?.('name'),
      optionValueId: selectedOption.dataset.optionValueId,
      variantId: selectedOption.dataset.variantId,
      connectedProductUrl: selectedOption.dataset.connectedProductUrl || null,
      selectedOptionsValues: this.selectedOptionsValues,
      datasetProductUrl: this.dataset.productUrl,
      pendingRequestUrl: this.#pendingRequestUrl,
      stack: new Error().stack,
      before: snapshotSizeState(),
    });

    this.updateSelectedOption(event.target);
    sizeDebug('after updateSelectedOption', snapshotSizeState(this));

    this.updateCrossOptionAvailability();
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.#requestedSelection = {
      optionValueIds: [...this.selectedOptionsValues],
      variantId: selectedOption.dataset.variantId,
    };

    const requestUrl = this.buildRequestUrl(selectedOption, null, [], { fullPage: loadsNewProduct });
    sizeDebug('will fetch', {
      seq,
      requestUrl,
      loadsNewProduct,
      currentUrl,
      newUrl,
      requestHasDoubleQuery: (requestUrl.match(/\?/g) || []).length > 1,
      search: new URL(requestUrl, window.location.origin).search,
    });

    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;
    /*frenzy */
    if ((!this.closest('quick-add-dialog')) && event.target.closest('variant-picker .variant-picker__form .variant-option--swatches')) {
      setTimeout(() => {
        if (typeof window.renderFrenzyBundle === 'function') window.renderFrenzyBundle();
        if (typeof window.renderRecommendations === 'function') window.renderRecommendations();
      }, 200);
    }
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        sizeDebug('history.replaceState', { seq, from: window.location.href, to: url.toString() });
        history.replaceState({}, '', url.toString());
      });
    }

    for (const delay of [0, 50, 150, 400, 800, 1500, 3000]) {
      setTimeout(() => {
        sizeDebug(`snapshot +${delay}ms after click #${seq}`, snapshotSizeState());
      }, delay);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
          const pId = window.frenzyCartProperties._sku?.split('_')[0];
          const vId = radios[newCurrentIndex].dataset.variantId;
          window.frenzyCartProperties._sku = pId + '_' + vId;
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {{ fullPage?: boolean }} [options] - Fetch the full page (combined listing product change).
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], options = {}) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const urlSource = selectedOption.dataset.connectedProductUrl
      ? 'connectedProductUrl'
      : this.#pendingRequestUrl
        ? 'pendingRequestUrl'
        : 'dataset.productUrl';
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }
    sizeDebug('buildRequestUrl', {
      urlSource,
      productUrl,
      connectedProductUrl: selectedOption.dataset.connectedProductUrl || null,
      pendingRequestUrl: this.#pendingRequestUrl,
      datasetProductUrl: this.dataset.productUrl,
      hasExistingQuery: Boolean(productUrl?.includes('?')),
      fullPage: Boolean(options.fullPage),
      sectionId: this.dataset.sectionId,
    });
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // Size/color changes on the PDP must hit the Section Rendering API. A full-page
    // fetch is cached by STOQ and comes back as selected_or_first_available_variant
    // (XS) regardless of option_values — which morphs the picker back to XS.
    if (!options.fullPage && this.dataset.sectionId) {
      params.unshift(`section_id=${this.dataset.sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const seq = sizeDebugSeq;
    const fetchStartedAt = performance.now();
    sizeDebug('fetch START', { seq, requestUrl, shouldMorphMain });

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        sizeDebug('fetch RESPONSE', {
          seq,
          ok: response.ok,
          status: response.status,
          finalUrl: response.url,
          ms: Math.round(performance.now() - fetchStartedAt),
        });
        return response.text();
      })
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const matchingPicker = this.#findMatchingPicker(html);
        const textContent = matchingPicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) {
          sizeDebug('fetch HTML has NO matching variant-picker JSON — aborting morph', {
            seq,
            variantPickerCount: html.querySelectorAll('variant-picker').length,
            matchingPicker: Boolean(matchingPicker),
          });
          return;
        }

        let parsedVariant = null;
        try {
          parsedVariant = JSON.parse(textContent);
        } catch {
          parsedVariant = null;
        }

        sizeDebug('fetch HTML selected variant', {
          seq,
          parsedVariant: parsedVariant
            ? { id: parsedVariant.id, title: parsedVariant.title, options: parsedVariant.options }
            : null,
          requested: this.#requestedSelection,
          htmlSnapshot: snapshotSizeState(html),
          beforeMorph: snapshotSizeState(),
        });

        if (shouldMorphMain) {
          sizeDebug('morph MAIN', { seq, stack: new Error().stack });
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.#restoreRequestedSelection();
          this.#rebuildRadioCache();

          const variant = await this.#resolveVariantForEvent(parsedVariant);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        sizeDebug('after morph', { seq, shouldMorphMain, state: snapshotSizeState() });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          sizeDebug('fetch ABORTED', { seq, requestUrl });
          console.warn('Fetch aborted by user');
        } else {
          sizeDebug('fetch ERROR', { seq, error: String(error), stack: error.stack });
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = this.#findMatchingPicker(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    sizeDebug('updateVariantPicker morph', {
      oldProductId: this.dataset.productId,
      newProductId: newVariantPickerSource instanceof HTMLElement ? newVariantPickerSource.dataset.productId : null,
      oldProductUrl: this.dataset.productUrl,
      newProductUrl: newVariantPickerSource instanceof HTMLElement ? newVariantPickerSource.dataset.productUrl : null,
      newProduct,
      before: snapshotSizeState(this),
    });

    morph(this, newVariantPickerSource);
    this.#rebuildRadioCache();
    this.updateCrossOptionAvailability();
    this.#loadColorVariants();

    sizeDebug('updateVariantPicker after morph', snapshotSizeState(this));

    queueMicrotask(() => {
      this.updateCombinedListingSwatchAvailability();
    });

    return newProduct;
  }

  get optionGroups() {
    return Array.from(this.querySelectorAll('fieldset.variant-option, .variant-option__select'));
  }

  getSelectedOptionsMap(optionNames = []) {
    const selectedOptions = new Map();

    this.optionGroups.forEach((group, index) => {
      const optionName = optionNames[index];

      if (!optionName) return;

      if (group instanceof HTMLFieldSetElement) {
        const checkedInput = group.querySelector('input:checked');
        if (checkedInput?.value) {
          selectedOptions.set(optionName, checkedInput.value);
        }
      } else if (group instanceof HTMLSelectElement && group.value) {
        selectedOptions.set(optionName, group.value);
      }
    });

    return selectedOptions;
  }

  setSwatchAvailability(input, isAvailable) {
    input.dataset.optionAvailable = String(isAvailable);

    if (isAvailable) {
      input.removeAttribute('aria-disabled');
    } else {
      input.setAttribute('aria-disabled', 'true');
    }

    const label = input.closest('label');
    if (!(label instanceof HTMLLabelElement)) return;

    const existingStrikethrough = label.querySelector('.variant-strikethrough');

    if (isAvailable) {
      existingStrikethrough?.remove();
      return;
    }

    if (existingStrikethrough) return;

    label.insertAdjacentHTML(
      'beforeend',
      `
        <svg
          class="variant-strikethrough"
          width="100%"
          height="100%"
          viewBox="0 0 100 46"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"></line>
          <line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"></line>
        </svg>
      `
    );
  }

  async updateCombinedListingSwatchAvailability() {
    // On product cards, color swatches should only be crossed out when the entire
    // color product is out of stock (server-provided availability), not by selected size.
    if (this.closest('product-card')) return;

    const swatchFieldset = this.querySelector('.variant-option--swatches');

    if (!(swatchFieldset instanceof HTMLFieldSetElement)) return;

    const swatchInputs = Array.from(
      swatchFieldset.querySelectorAll('input[type="radio"][data-option-value-id]')
    );

    if (!swatchInputs.some((input) => input.dataset.connectedProductUrl)) return;

    const currentProductUrl = this.dataset.productUrl;
    if (!currentProductUrl) return;

    const requestId = ++this.#availabilityRequestId;

    try {
      const currentProductData = await fetchProductData(currentProductUrl);

      if (requestId !== this.#availabilityRequestId) return;

      const optionNames = getProductOptionNames(currentProductData);
      const optionGroups = this.optionGroups;
      const swatchOptionIndex = optionGroups.indexOf(swatchFieldset);
      const swatchOptionName = optionNames[swatchOptionIndex];

      if (!swatchOptionName) return;

      const selectedOptions = this.getSelectedOptionsMap(optionNames);

      await Promise.all(
        swatchInputs.map(async (input) => {
          const swatchProductUrl = input.dataset.connectedProductUrl || currentProductUrl;
          const swatchProductData =
            normalizeProductUrl(swatchProductUrl) === normalizeProductUrl(currentProductUrl)
              ? currentProductData
              : await fetchProductData(swatchProductUrl);

          if (requestId !== this.#availabilityRequestId) return;

          const swatchSelections = new Map(selectedOptions);
          swatchSelections.set(swatchOptionName, input.value);

          const matchingVariant = findMatchingVariant(swatchProductData, swatchSelections);
          this.setSwatchAvailability(input, Boolean(matchingVariant?.available));
        })
      );
    } catch (error) {
      console.error('Failed to update combined listing swatch availability', error);
    }
  }

  #rebuildRadioCache() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-option'));
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      this.#radios.push(radios);
      const currentIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (currentIndex !== -1) this.#checkedIndices.push([currentIndex]);
    });
  }

  /**
   * Finds this picker's counterpart in fetched HTML. A full-page response contains
   * recommendation/mix-match pickers; grabbing the first one returns the wrong JSON
   * (selected_or_first_available_variant of a different card).
   * @param {Document | Element} html
   * @returns {Element | null}
   */
  #findMatchingPicker(html) {
    const pickers = Array.from(html.querySelectorAll(this.tagName.toLowerCase()));
    const byBlock = pickers.find((picker) => picker.dataset.blockId && picker.dataset.blockId === this.dataset.blockId);
    if (byBlock) return byBlock;

    const byProduct = pickers.find(
      (picker) => picker.dataset.productId === this.dataset.productId && !picker.closest('product-card')
    );
    if (byProduct) return byProduct;

    return pickers[0] ?? null;
  }

  /**
   * Re-checks the option values the user just selected. STOQ/full-page Liquid cache
   * ignores option_values and morphs the picker back to XS.
   */
  #restoreRequestedSelection() {
    const requested = this.#requestedSelection;
    if (!requested?.optionValueIds?.length) return;

    for (const optionValueId of requested.optionValueIds) {
      const input = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (input instanceof HTMLInputElement || input instanceof HTMLOptionElement) {
        if (input instanceof HTMLInputElement) input.checked = true;
        if (input instanceof HTMLOptionElement) input.selected = true;
      }
    }

    sizeDebug('restoreRequestedSelection', {
      requested,
      after: snapshotSizeState(this),
    });
  }

  /**
   * If Liquid JSON is the default/first variant (XS) but the user clicked another
   * size, resolve the real variant from product.js so the form id/price/media match.
   * @param {object | null} parsedVariant
   */
  async #resolveVariantForEvent(parsedVariant) {
    const requestedVariantId = this.#requestedSelection?.variantId;
    if (!requestedVariantId || !parsedVariant) return parsedVariant;
    if (String(parsedVariant.id) === String(requestedVariantId)) return parsedVariant;

    sizeDebug('Liquid JSON mismatch', {
      liquidId: parsedVariant.id,
      liquidTitle: parsedVariant.title,
      clickedVariantId: requestedVariantId,
    });

    try {
      const productData = await fetchProductData(this.dataset.productUrl);
      const match = productData.variants?.find((variant) => String(variant.id) === String(requestedVariantId));
      if (match) {
        sizeDebug('resolved variant from product.js', { id: match.id, title: match.title });
        return match;
      }
    } catch (error) {
      sizeDebug('product.js lookup failed', { error: String(error) });
    }

    return { ...parsedVariant, id: Number(requestedVariantId) };
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Fetches variant data for all connected color products so we can determine
   * cross-option availability (e.g. which colors have the selected size in stock).
   * Results are cached to avoid redundant requests.
   */
  async #loadColorVariants() {
    const colorRadios = Array.from(
      this.querySelectorAll('fieldset.variant-option input[data-connected-product-url]')
    );

    const uniqueUrls = [
      ...new Set(
        colorRadios
          .map((r) => r.dataset.connectedProductUrl?.split('?')[0])
          .filter((url) => url && !this.#colorVariantCache.has(url))
      ),
    ];

    if (!uniqueUrls.length) return;

    await Promise.all(
      uniqueUrls.map(async (url) => {
        try {
          const res = await fetch(`${url}.js`);
          const data = await res.json();
          this.#colorVariantCache.set(
            url,
            (data.variants || []).map((v) => ({ options: v.options, available: v.available }))
          );
        } catch {
          // Ignore fetch errors — will fall back to data-option-available
        }
      })
    );

    this.updateCrossOptionAvailability();
  }

  /**
   * Updates strikethrough state for all variant options.
   *
   * For non-color options (e.g. size): mirrors `data-option-available` directly —
   * Shopify sets this correctly for the current product.
   *
   * For color swatches in combined listings: uses cached product variant data to
   * check whether each color has the currently selected size in stock, since Shopify
   * does not cross-reference size availability across combined listing child products
   * when setting `data-option-available` on color swatches.
   */
  updateCrossOptionAvailability() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-option'));
    const isProductCardPicker = Boolean(this.closest('product-card'));

    // Identify the size fieldset (the one whose radios have no connected product URL)
    // and record its index (= position in variant.options[] for connected products).
    let sizeOptionIndex = -1;
    let selectedSizeValue = null;

    for (let i = 0; i < fieldsets.length; i++) {
      const radios = Array.from(fieldsets[i].querySelectorAll('input[type="radio"]'));
      const hasConnectedUrl = radios.some((r) => r.dataset.connectedProductUrl);
      if (!hasConnectedUrl) {
        sizeOptionIndex = i;
        selectedSizeValue = radios.find((r) => r.checked)?.value ?? null;
        break;
      }
    }

    const radios = Array.from(this.querySelectorAll('fieldset.variant-option input[type="radio"]'));

    radios.forEach((radio) => {
      const label = radio.closest('label');
      if (!label) return;

      let isOOS = radio.dataset.optionAvailable === 'false';

      // For color swatches: check cross-product availability using cached variant data.
      // Shopify's data-option-available on color swatches in combined listings only
      // reflects whether the color product has ANY stock, not stock in the selected size.
      const connectedUrl = radio.dataset.connectedProductUrl?.split('?')[0];
      if (!isProductCardPicker && connectedUrl && selectedSizeValue && sizeOptionIndex >= 0) {
        const variants = this.#colorVariantCache.get(connectedUrl);
        if (variants) {
          const hasAvailableInSize = variants.some(
            (v) => v.options[sizeOptionIndex] === selectedSizeValue && v.available
          );
          isOOS = !hasAvailableInSize;
        }
      }

      if (isOOS) {
        if (!label.querySelector('.variant-strikethrough')) {
          label.appendChild(this.#createStrikethroughSvg());
        }
      } else {
        label.querySelector('.variant-strikethrough--js')?.remove();
      }
    });
  }

  /**
   * Creates a strikethrough SVG element matching the server-rendered version.
   * @returns {SVGElement}
   */
  #createStrikethroughSvg() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'variant-strikethrough variant-strikethrough--js');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', '0 0 100 46');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 2; i++) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', '100');
      line.setAttribute('y1', '0');
      line.setAttribute('x2', '0');
      line.setAttribute('y2', '46');
      line.setAttribute('vector-effect', 'non-scaling-stroke');
      svg.appendChild(line);
    }
    return svg;
  }

}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

if (SIZE_DEBUG && !window.__sizeDebugInstalled) {
  window.__sizeDebugInstalled = true;
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      if (!target.closest('variant-picker')) return;
      sizeDebug('document capture change', {
        isTrusted: event.isTrusted,
        value: target.value,
        name: target.name,
        variantId: target.dataset.variantId,
        stack: new Error().stack,
      });
    },
    true
  );
  sizeDebug('module loaded', snapshotSizeState());
}
